/**
 * A scope's git repository on disk (git sync Stage 2, S2.3; owner decisions
 * 02.10.2026): "My boards & documents" (the personal scope) and each team
 * have ONE repository each. Device-only items never go to git.
 *
 *   <account>/scope-mirror/personal       the personal scope
 *   <account>/scope-mirror/team-<id>      a team
 *
 * The folder is the only place the Rust git guard and the webview's fs scope
 * allow for it (fs_guard::check_git_repo_path, capabilities/default.json).
 *
 * WHAT goes in comes from contributors — documents (S2.4), boards (S2.5),
 * projects (S2.6) — each adding its files to the map; this module writes the
 * manifest and the git setup files, and nothing else.
 */
import { accountPath } from '../accountData';
import { personalScopeId, PERSONAL_LOCAL_SCOPE } from '../personalScope';
import { getAllScopeGitLinks, setScopeGitLink } from '../accountsStore';
import { writeWorkspaceFiles } from '../gitFileWriter';
import { reportScopeProgress } from './scopeProgress';
import { SCOPE_ROOTS, SCOPE_TOP_FILES, SCOPE_GITATTRIBUTES, SCOPE_GITIGNORE, isScopeManaged } from './scopeRepoLayout';

const PERSONAL = /^personal:/;
const TEAM = /^team:(.+)$/;

/** The folder name of a scope key; null for a scope that never syncs (device-only). */
export function scopeFolder(scopeKey) {
  const key = String(scopeKey || '');
  // Signed out ("personal:local") is a different set of items than an account's: its own folder.
  if (key === PERSONAL_LOCAL_SCOPE) return 'personal-local';
  if (PERSONAL.test(key)) return 'personal';
  const team = TEAM.exec(key);
  if (team && /^[A-Za-z0-9_-]{1,64}$/.test(team[1])) return `team-${team[1]}`;
  return null;
}

export async function scopeMirrorDir(scopeKey) {
  const folder = scopeFolder(scopeKey);
  if (!folder) throw new Error(`scope_not_syncable: ${scopeKey}`);
  const dir = await accountPath(`scope-mirror/${folder}`);
  if (!dir) throw new Error('scope_mirror_unavailable');
  return dir.replace(/\\/g, '/');
}

/**
 * A personal scope is only ever the one signed in now: a link saved for
 * another account (or for "signed out") is never synced against this one's items.
 */
export function isCurrentScope(scopeKey) {
  const key = String(scopeKey || '');
  if (PERSONAL.test(key)) return key === personalScopeId();
  return TEAM.test(key);
}

/* ── what goes in ──────────────────────────────────────────────────────── */

const contributors = new Map();

/**
 * @param {{ id: string,
 *           files: (scopeKey: string) => Promise<Record<string, string|Uint8Array>>,
 *           apply?: (scopeKey: string, change: { before, after, dir }) => Promise<object> }} contributor
 *   `files` returns its part of the repository (relative path → text or bytes);
 *   `apply` takes what a pull changed (text files before / after the merge) into the app.
 */
export function registerScopeContributor(contributor) {
  contributors.set(contributor.id, contributor);
  return () => contributors.delete(contributor.id);
}

/** What a pull changed, handed to every contributor that applies; `{ [id]: its result }`. */
export async function applyScopeChanges(scopeKey, change) {
  const out = {};
  for (const c of contributors.values()) {
    if (c.apply) out[c.id] = await c.apply(scopeKey, change);
  }
  return out;
}

export function manifestFor(scopeKey, name) {
  const team = TEAM.exec(String(scopeKey || ''));
  return [
    'apiboo: scope',
    'version: 1',
    `scope: ${team ? 'team' : 'personal'}`,
    ...(team ? [`team: ${team[1]}`] : []),
    `name: ${JSON.stringify(String(name || (team ? 'Team' : 'My projects, boards & documents')))}`,
    '',
  ].join('\n');
}

/** The name a manifest carries (JSON-quoted, as manifestFor writes it). */
export function nameInManifest(text) {
  const m = /^name:\s*(.+)$/m.exec(String(text || ''));
  if (!m) return null;
  try {
    return String(JSON.parse(m[1]));
  } catch {
    return m[1].trim();
  }
}

/** Everything that belongs in the repository right now. */
export async function scopeFiles(scopeKey, name) {
  const files = {
    'apiboo.yaml': manifestFor(scopeKey, name),
    '.gitattributes': SCOPE_GITATTRIBUTES,
    '.gitignore': SCOPE_GITIGNORE,
  };
  for (const c of contributors.values()) {
    for (const [path, content] of Object.entries((await c.files(scopeKey)) || {})) {
      if (!isScopeManaged(path)) throw new Error(`${c.id} wrote outside its folders: ${path}`);
      files[path] = content;
    }
  }
  return files;
}

/** Bring the scope's folder in line with what it should hold (only changed files; only Apiboo's own deleted). */
export async function refreshScopeMirror(scopeKey, name) {
  const fs = await import('@tauri-apps/plugin-fs');
  const dir = await scopeMirrorDir(scopeKey);
  await fs.mkdir(dir, { recursive: true });
  // No name given (a background refresh): keep the one the manifest has, so it never flips to a default.
  const keptName = name ?? (await fs.readTextFile(`${dir}/apiboo.yaml`).then(nameInManifest, () => null));
  const files = await scopeFiles(scopeKey, keptName);
  reportScopeProgress(scopeKey, { step: 'writing' });
  return writeWorkspaceFiles(fs, dir, files, {
    roots: SCOPE_ROOTS,
    isManaged: isScopeManaged,
    topFiles: SCOPE_TOP_FILES,
  });
}

/* ── which repository a scope is linked to ─────────────────────────────── */

export async function scopeLink(scopeKey) {
  return (await getAllScopeGitLinks())[String(scopeKey)] || null;
}

export async function saveScopeLink(scopeKey, git) {
  await setScopeGitLink(String(scopeKey), git || null);
}

/**
 * The scopes the Git page offers: yours, and each team you belong to (owner
 * decision 02.10.2026: only team owners and admins connect a team; since card
 * 84628c27 every member may Join the repository stored on the team).
 * @param {{ personalKey: string, teams: Array<{id, name, role, git_repo?}> }} p
 */
export function connectableScopes({ personalKey, teams = [] }) {
  const out = [];
  if (personalKey && scopeFolder(personalKey)) out.push({ key: personalKey, name: 'My projects, boards & documents', kind: 'personal' });
  for (const t of teams) {
    const key = `team:${t.id}`;
    if (!scopeFolder(key)) continue;
    // teamRepo: the repository stored on the team (card 84628c27) — every member may Join it.
    out.push({ key, name: t.name || 'Team', kind: 'team', canConnect: t.role === 'owner' || t.role === 'admin', teamRepo: t.git_repo?.url ? t.git_repo : null });
  }
  return out;
}
